import { Component, OnInit, Output, EventEmitter, OnDestroy, NgZone, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { Subscription, interval } from 'rxjs';
import { SatDatepicker } from 'saturn-datepicker';
import { isNullOrUndefined } from 'util';

const isSmallScreenMediaQuery: string = '(max-width: 750px)';

const AUTORENEW_INTERVAL_SECONDS = 30;

export interface SatDatepickerRangeValue<D> {
  begin: D | null;
  end: D | null;
}

export interface DatepickerUpdate {
  toDate: Date;
  fromDate: Date;
}

export interface TimeFilterUpdate {
  toTime: any;
  fromTime: any;
}

@Component({
  selector: 'app-controls',
  templateUrl: './controls.component.html',
  styleUrls: ['./controls.component.scss']
})
export class ControlsComponent implements OnInit, OnDestroy {

  @ViewChild(SatDatepicker) picker;

  @Output()
  dateSelected = new EventEmitter<DatepickerUpdate>();

  @Output()
  autoRenewUpdate = new EventEmitter<DatepickerUpdate>();

  @Output()
  timeFilterUpdate = new EventEmitter<TimeFilterUpdate>();

  @Output()
  isSmallLayoutEvent = new EventEmitter<boolean>();
  isSmallLayout: boolean = true;
  matcher: MediaQueryList;
  resizeListener: any;

  autoRenewCannotBeEnabled: boolean = false;
  autoRenewEnabled: boolean = false;
  autoRenewSubscription: Subscription;

  minDate = new Date("June 1, 2017");
  maxDate = new Date(); // today

  // defaultDate = { begin: new Date("August 1, 2017"), end: new Date() };
  defaultDate = { begin: new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate() - 30), end: this.maxDate };
  currentDate = this.defaultDate;

  date = this.defaultDate; // initial value

  filterTimeEnabled: boolean = false;
  timeFilterForm = new FormGroup({
    fromTime: new FormControl({value: '13:00', disabled: true}),
    toTime: new FormControl({value: '15:00', disabled: true})
  });

  singleDaySelected: boolean = false;

  constructor(private breakpointObserver: BreakpointObserver,
    private mediaMatcher: MediaMatcher,
    private zone: NgZone,) { }

  ngOnInit() {
    this.enableResizeListener();

    this.onDateChange(this.date);
    this.timeFilterForm.valueChanges.subscribe(value => this.onTimeFilterChange(value));
  }

  ngOnDestroy() {
    this.autoRenewSubscription.unsubscribe();
  }

  // Change layout based on isSmallScreenMediaQuery
  enableResizeListener() {
    // On init, check size of viewport and use the appropriately sized layout
    const initIsLargeScreen = this.breakpointObserver.isMatched(isSmallScreenMediaQuery);
    this.changeLayout(initIsLargeScreen);

    // After init, listen to changes in size of viewport
    // and resize to small or large layout when appropriate
    this.resizeListener = (match: MediaQueryList) => {
      // Run the query "in the zone"
      // This is necessary for Angular to realize changes to layout
      this.zone.run(() => {
        // Change layout to match the media query
        this.changeLayout(match.matches);
      })
    };

    this.matcher = this.mediaMatcher.matchMedia(isSmallScreenMediaQuery);
    this.matcher.addListener(this.resizeListener);
  }

  changeLayout(isSmall: boolean) {
    this.isSmallLayout = isSmall;
    this.isSmallLayoutEvent.emit(isSmall);
  }

  setDate(dayOffset: number) {
    // Reset maxdate to now
    this.maxDate = new Date();
    this.date = { begin: new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate() - dayOffset), end: this.maxDate };
    this.onDateChange(this.date);
  }

  onDateChange(event: SatDatepickerRangeValue<Date>) {
    let dateRange: DatepickerUpdate = {fromDate: event.begin, toDate: event.end};
    this.currentDate = event;
    this.checktoDateForAutoRenew(event.end);
    this.dateSelected.emit(dateRange);
    let dateDiff = event.end.getTime() - event.begin.getTime();
    this.singleDaySelected = Math.round((dateDiff / (1000 * 60 * 60 * 24))) == 1;
  }

  checktoDateForAutoRenew(toDate: Date) {
    let today = new Date();
    if(this.isDateEquivalent(today, toDate)) {
      this.autoRenewCannotBeEnabled = false;
    }
    else {
      this.disableAutoRenew();
      this.autoRenewCannotBeEnabled = true;
    }
  }

  onTimeFilterChange(time: TimeFilterUpdate) {
    // console.log("filter status: ", this.filterTimeEnabled, "filter change: ", time);
    if(this.filterTimeEnabled) {
      // Check if fromTime and toTime are reversed. If they are, switch them back.
      // This allows the time inputs to be used whichever way the user wants
      if(!isNullOrUndefined(time) && !isNullOrUndefined(time.fromTime) && !isNullOrUndefined(time.toTime) && time.fromTime > time.toTime) {
        let reversedTime = {toTime: time.fromTime, fromTime: time.toTime};
        this.timeFilterUpdate.emit(reversedTime);
      }
      else {
        this.timeFilterUpdate.emit(time);
      } 
    }
    else {
      this.timeFilterUpdate.emit(null); // return null if the filter has been disabled
    }
  }

  // Auto Renew Button
  onAutoRenewButtonClick() {
    if(this.autoRenewEnabled) {
      this.disableAutoRenew();
    }
    else {
      this.enableAutoRenew();
    }
  }

  enableAutoRenew() {
    this.autoRenewSubscription = interval(AUTORENEW_INTERVAL_SECONDS * 1000).subscribe(() => {
      this.onAutoRenew();
    });
    this.autoRenewEnabled = true;
  }

  disableAutoRenew() {
    this.autoRenewSubscription.unsubscribe();
    this.autoRenewEnabled = false;
  }

  // Auto Renew Function
  onAutoRenew() {
    let latestDateRange: DatepickerUpdate = {fromDate: this.currentDate.begin, toDate: new Date()};
    this.autoRenewUpdate.emit(latestDateRange);
  }

  // Time Filter Button
  onFilterButtonClick() {
    if(this.filterTimeEnabled) {
      this.disableTimeFilter();
    }
    else {
      this.enableTimeFilter();
    }
  }

  enableTimeFilter() {
    this.filterTimeEnabled = true;
    this.timeFilterForm.enable();
    this.disableAutoRenew();
  }

  disableTimeFilter() {
    this.filterTimeEnabled = false;
    this.timeFilterForm.disable();
  }

  // Enable Time Filter if the user clicks the disabled input
  onTimeInputClick() {
    if(!this.filterTimeEnabled) {
      this.enableTimeFilter();
    }
  }

  onResetDateClick() {
    this.date = {begin: this.defaultDate.begin, end: new Date()};
    this.defaultDate = this.date;
    this.onDateChange(this.date);
    this.enableAutoRenew();
  }

  isDateDefault(): boolean {
    return this.isDateEquivalent(this.currentDate.end, this.defaultDate.end);
  }

  isDateEquivalent(date1: Date, date2: Date) {
    return date1.getFullYear() == date2.getFullYear() && 
      date1.getMonth() == date2.getMonth() && 
      date1.getDate() == date2.getDate();
  }

  openDatepicker() {
    this.picker.open();
  }
}
